import { Routes, Route, useLocation, useNavigate } from "react-router-dom"
import FeedbackForm from "./FeedbackForm"
import InvoicingForm from "./InvoicingForm"
import OrganizationForm from "./OrganizationForm"
import VaaratapahtumaForm from "./VaaratapahtumaForm"
import KorjauspyyntoForm from "./KorjauspyyntoForm"
import MuistutusForm from "./MuistutusForm"
import RootHandler from "./RootHandler"
import LanguageWrapper from "./LanguageWrapper"
import RouteRedirect from "./RouteRedirect"
import FallbackRedirect from "./FallbackRedirect"
import FormGuard from "./FormGuard"
import { useState, useEffect, useMemo } from "react"
import { Button, Icon } from "@terveystalo/design-system-react-components"
import authClient, { authConfig } from "./UserManager"
import { User } from "@terveystalo/web-auth-onewelcome"
import { useTranslation } from "react-i18next"

function App() {
  const [user, setUser] = useState<User | null>(null)
  const [hideNavLogin, setHideNavLogin] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const { t, i18n } = useTranslation(undefined, { bindI18n: "languageChanged loaded" })

  // Sync i18n language with URL when navigating between language routes
  useEffect(() => {
    const pathParts = location.pathname.split("/").filter(Boolean)
    const langFromUrl = pathParts[0]
    const supportedLanguages = ["en", "fi", "se"]

    if (langFromUrl && supportedLanguages.includes(langFromUrl) && i18n.language !== langFromUrl) {
      i18n.changeLanguage(langFromUrl)
    }
  }, [location.pathname, i18n])

  // Update page title when language changes
  useEffect(() => {
    document.title = t("pageTitle")
  }, [i18n.language, t])

  useEffect(() => {
    const loadUser = async () => {
      try {
        // Try to get user silently (will refresh session if needed)
        const userFromStorage = await authClient.getUserSilently()
        setUser(userFromStorage)
      } catch (error) {
        console.error("Error loading user:", error)
        setUser(null)
      }
    }
    loadUser()
  }, [])

  const login = async () => {
    // Store current location before redirecting to authentication
    console.log("[App.tsx] Authenticate clicked - Current location:", location.pathname)
    sessionStorage.setItem("returnUrl", location.pathname)
    console.log("[App.tsx] Stored returnUrl in sessionStorage:", location.pathname)

    try {
      // Dispatch event to save form state before redirecting
      // Wait for the form to save its state
      console.log("[App.tsx] Dispatching save-form-state event...")
      const formStateSaved = new Promise<void>((resolve) => {
        const timeout = setTimeout(() => {
          console.log("[App.tsx] Form state save timeout - proceeding with redirect")
          resolve()
        }, 1000) // Timeout after 1 second if no form responds

        const handleFormStateSaved = () => {
          console.log("[App.tsx] Received form-state-saved event")
          clearTimeout(timeout)
          window.removeEventListener("form-state-saved", handleFormStateSaved)
          resolve()
        }
        window.addEventListener("form-state-saved", handleFormStateSaved)
      })

      window.dispatchEvent(new CustomEvent("save-form-state"))
      await formStateSaved

      console.log("[App.tsx] Calling authClient.signinRedirect()...")
      await authClient.signinRedirect()
      console.log("[App.tsx] signinRedirect completed (should have redirected)")
    } catch (error) {
      console.error("[App.tsx] Error during signinRedirect:", error)
    }
  }

  const logout = () => {
    authClient.signoutRedirect({ post_logout_redirect_uri: authConfig.post_logout_redirect_uri })
  }

  const isLoggedIn = useMemo(() => Boolean(user), [user])

  // Hide login button for routes that don't require authentication
  const hideLoginButton = useMemo(() => {
    const pathParts = location.pathname.split("/").filter(Boolean)
    const currentRoute = pathParts.length > 1 ? pathParts[1] : null

    const invoicingCanonical = t("routes.invoicing.canonical")
    const invoicingAliases = t("routes.invoicing.aliases", { returnObjects: true }) as string[]

    return currentRoute === invoicingCanonical || invoicingAliases.includes(currentRoute || "")
  }, [location.pathname, t])

  const changeLanguage = (lng: string) => {
    // Extract current route path after language segment
    const pathParts = location.pathname.split("/").filter(Boolean)
    const currentRoute = pathParts.length > 1 ? pathParts[1] : null

    // Map current route to route key by checking both canonical and aliases
    const routeKeys = [
      "customer",
      "invoicing",
      "org",
      "vaaratapahtuma",
      "korjauspyynto",
      "muistutus",
    ] as const
    let targetRouteKey: (typeof routeKeys)[number] = "customer"

    for (const key of routeKeys) {
      const canonical = t(`routes.${key}.canonical`)
      const aliases = t(`routes.${key}.aliases`, { returnObjects: true }) as string[]

      if (currentRoute === canonical || aliases.includes(currentRoute || "")) {
        targetRouteKey = key
        break
      }
    }

    // Get the canonical path for target route in new language
    // Temporarily switch language to get the target canonical path
    const targetCanonical = i18n.getResource(
      lng,
      "translation",
      `routes.${targetRouteKey}.canonical`,
    )

    // Navigate to same route type with new language
    navigate(`/${lng}/${targetCanonical}`)
  }

  return (
    <div>
      <nav
        className="no-print"
        style={{
          margin: "10px 0",
          padding: "10px",
          borderBottom: "1px solid #eee",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <div>
          {!hideNavLogin && !hideLoginButton && (
            <>
              {!isLoggedIn ? (
                <Button onClick={login}>
                  <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <Icon name="LogIn" color="white" />
                    {t("login")}
                  </span>
                </Button>
              ) : (
                <Button onClick={logout} variant="secondary">
                  <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <Icon name="LogOut" color="lake" />
                    {t("logout")}
                  </span>
                </Button>
              )}
            </>
          )}
        </div>
        <div>
          <button
            onClick={() => changeLanguage("fi")}
            className={`lang-button ${i18n.language === "fi" ? "selected" : ""}`}
            style={{
              padding: "5px 10px",
              background: i18n.language === "fi" ? "#ddd" : "#fff",
              fontFamily: "TerveystaloSans, sans-serif",
            }}
          >
            FI
          </button>
          <button
            onClick={() => changeLanguage("se")}
            className={`lang-button ${i18n.language === "se" ? "selected" : ""}`}
            style={{
              padding: "5px 10px",
              background: i18n.language === "se" ? "#ddd" : "#fff",
              fontFamily: "TerveystaloSans, sans-serif",
            }}
          >
            SE
          </button>
          <button
            onClick={() => changeLanguage("en")}
            className={`lang-button ${i18n.language === "en" ? "selected" : ""}`}
            style={{
              marginRight: "10px",
              padding: "5px 10px",
              background: i18n.language === "en" ? "#ddd" : "#fff",
              fontFamily: "TerveystaloSans, sans-serif",
            }}
          >
            EN
          </button>
        </div>
      </nav>

      <Routes>
        {/* Root handler for auth callbacks and language detection */}
        <Route path="/" element={<RootHandler />} />

        {/* Silent redirect for session refresh */}
        <Route path="/signin-silent-redirect" element={<div>Processing...</div>} />

        {/* Language-based routes */}
        <Route path="/:lang" element={<LanguageWrapper />}>
          {/* Canonical routes - these are the localized paths */}
          <Route
            path={t("routes.customer.canonical")}
            element={
              <FormGuard formKey="customer">
                <FeedbackForm user={user} isLoggedIn={isLoggedIn} />
              </FormGuard>
            }
          />
          <Route
            path={t("routes.invoicing.canonical")}
            element={
              <FormGuard formKey="invoicing">
                <InvoicingForm isLoggedIn={isLoggedIn} />
              </FormGuard>
            }
          />
          <Route
            path={t("routes.org.canonical")}
            element={
              <FormGuard formKey="org">
                <OrganizationForm user={user} isLoggedIn={isLoggedIn} />
              </FormGuard>
            }
          />
          <Route
            path={t("routes.vaaratapahtuma.canonical")}
            element={
              <FormGuard formKey="vaaratapahtuma">
                <VaaratapahtumaForm
                  user={user}
                  isLoggedIn={isLoggedIn}
                  setHideNavLogin={setHideNavLogin}
                />
              </FormGuard>
            }
          />
          <Route
            path={t("routes.korjauspyynto.canonical")}
            element={
              <FormGuard formKey="korjauspyynto">
                <KorjauspyyntoForm user={user} isLoggedIn={isLoggedIn} />
              </FormGuard>
            }
          />
          <Route
            path={t("routes.muistutus.canonical")}
            element={
              <FormGuard formKey="muistutus">
                <MuistutusForm user={user} isLoggedIn={isLoggedIn} />
              </FormGuard>
            }
          />

          {/* Redirect routes - all aliases redirect to canonical paths */}
          {(t("routes.customer.aliases", { returnObjects: true }) as string[]).map((alias) => (
            <Route key={alias} path={alias} element={<RouteRedirect to="customer" />} />
          ))}
          {(t("routes.invoicing.aliases", { returnObjects: true }) as string[]).map((alias) => (
            <Route key={alias} path={alias} element={<RouteRedirect to="invoicing" />} />
          ))}
          {(t("routes.org.aliases", { returnObjects: true }) as string[]).map((alias) => (
            <Route key={alias} path={alias} element={<RouteRedirect to="org" />} />
          ))}
          {(t("routes.vaaratapahtuma.aliases", { returnObjects: true }) as string[]).map(
            (alias) => (
              <Route key={alias} path={alias} element={<RouteRedirect to="vaaratapahtuma" />} />
            ),
          )}
          {(t("routes.korjauspyynto.aliases", { returnObjects: true }) as string[]).map((alias) => (
            <Route key={alias} path={alias} element={<RouteRedirect to="korjauspyynto" />} />
          ))}
          {(t("routes.muistutus.aliases", { returnObjects: true }) as string[]).map((alias) => (
            <Route key={alias} path={alias} element={<RouteRedirect to="muistutus" />} />
          ))}

          {/* Catch-all for /:lang/ (empty path under language) */}
          <Route index element={<FallbackRedirect />} />

          {/* Catch-all for any unmatched routes under /:lang/ */}
          <Route path="*" element={<FallbackRedirect />} />
        </Route>
      </Routes>
    </div>
  )
}

export default App
